import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { History as HistoryIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  GridLegacy as Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { COUNTRIES } from '~/consts/priceParrot';

import { RATE_PLAN_ACTIVE_STATUS, useGetPropertyRatePlans } from '~/hooks/apis/reservation/usePropertyRatePlans';

import OffersService from '~/services/OffersService';
import PriceParrotService from '~/services/PriceParrotService';
import SearchService from '~/services/SearchService';
import { formatDateISO } from '~/services/TimeService';
import VendorsService from '~/services/VendorsService';

import { PriceSummaryJob, PriceSummaryJobStatus, Property } from '~/types/services/priceParrot';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';
import { parsePropertyNameFromTAURL } from '~/utils/priceParrot';

import LocationAutocomplete from '../LocationAutocomplete';

import EmailGenerator from './EmailGenerator';
import ParityReport from './ParityReport';
import RecentScrapes from './RecentScrapes';
import { analyzeParityCsv } from './analyzeParityCsv';
import { exportToPdf } from './exportPdf';
import { ParityAnalysis, RevenueHooks } from './types';

const NIGHT_COUNTS = [1, 2, 3, 4, 5] as const;
const DEFAULT_NIGHTS = 3;

type FormInput = {
  locationUrl: string;
  daysAhead: number;
  currencyCode: string;
  countryCode: string;
};

type JobStatus = 'idle' | 'submitting' | 'scraping' | 'analyzing' | 'done' | 'error';

// AGENT READ THIS: never remove below lines, only comment out when time to push to prod
// Staging mock: no valid offer IDs in staging, so return a hardcoded one
// return '0062y000004Rx7FAAS';
async function resolveOfferIdByName(propertyName: string): Promise<string | null> {
  // Staging mock: uncomment the line below and comment out the try/catch block for staging testing
  // return '0062y000004Rx7FAAS';
  try {
    const { result } = await SearchService.typeahead(propertyName, {
      type: ['le_property'],
      brand: 'luxuryescapes',
      region: 'AU',
      limit: 1,
      priority: 'current',
    });
    return result?.[0]?.offerId ?? null;
  } catch {
    return null;
  }
}

export default function PriceParrotParityTool() {
  const { control, handleSubmit, getValues, setValue } = useForm<FormInput>({
    defaultValues: {
      locationUrl: '',
      daysAhead: 364,
      currencyCode: 'AUD',
      countryCode: 'AU',
    },
  });

  const [status, setStatus] = useState<JobStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<Map<number, ParityAnalysis>>(new Map());
  const [selectedNights, setSelectedNights] = useState<number>(DEFAULT_NIGHTS);
  const [revenueHooks, setRevenueHooks] = useState<RevenueHooks | null>(null);
  const [vendorAccountId, setVendorAccountId] = useState<string | undefined>();
  const [emailExpanded, setEmailExpanded] = useState(false);
  const {
    data: fetchedRatePlans = [],
    isLoading: ratePlansLoading,
    error: ratePlansError,
  } = useGetPropertyRatePlans(vendorAccountId);

  // AGENT READ THIS: never remove below lines, only comment out when time to push to prod
  // Staging mock: uncomment the block below and comment out `const ratePlans = fetchedRatePlans;` for staging testing
  // const ratePlans = [
  //   { id: '1', name: 'BAR-STANDARD', rate_plan_code: 'BAR2024', source: 'lpc', status: 'active', price: 57245 },
  //   { id: '2', name: 'Early Bird Discount 15%', rate_plan_code: 'EB15', source: 'lpc', status: 'active', price: 48658 },
  //   { id: '3', name: 'Weekend Special', rate_plan_code: 'WKD20', source: 'lpc', status: 'active', price: 52100 },
  // ];
  const ratePlans = fetchedRatePlans;

  const activePlans = ratePlans.filter((p) => p.status?.toLowerCase() === RATE_PLAN_ACTIVE_STATUS);
  const cheapestPlan =
    [...activePlans].sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity))[0] ?? ratePlans[0];
  const selectedProperty = useRef<Property | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);

  const currentAnalysis = analyses.get(selectedNights) ?? null;
  const availableNights = Array.from(analyses.keys()).sort((a, b) => a - b);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const fetchSupplementaryData = useCallback(async (propertyName: string) => {
    const offerId = await resolveOfferIdByName(propertyName);
    if (!offerId) {
      return;
    }

    try {
      const [parityResponse, offerResponse] = await Promise.allSettled([
        VendorsService.getParity(offerId, 'value'),
        OffersService.getOffer(offerId),
      ]);

      if (parityResponse.status === 'fulfilled') {
        setRevenueHooks(parityResponse.value.data);
      }

      if (offerResponse.status === 'fulfilled') {
        setVendorAccountId(offerResponse.value.result?.vendor_account_id);
      }
    } catch {
      // Supplementary data is optional
    }
  }, []);

  const analyzeAndShow = useCallback(async (jobId: string, propName: string, currency: string) => {
    const response = await PriceParrotService.downloadPriceSummaryJob(jobId);
    const csvText = await response.text();
    return analyzeParityCsv(csvText, propName, currency);
  }, []);

  const loadReportForProperty = useCallback(
    async (locationUrl: string, currencyCode: string, propertyName: string) => {
      cleanup();
      setError(null);
      setAnalyses(new Map());
      setRevenueHooks(null);
      setVendorAccountId(undefined);
      setStatus('analyzing');
      setProgress(50);
      setProgressText('Looking for existing reports...');

      const { data: allJobs } = await PriceParrotService.getPriceSummaryJobs({ limit: 50 });
      const completedForProperty = allJobs.filter(
        (j) => j.locationUrl === locationUrl && j.status === PriceSummaryJobStatus.COMPLETED,
      );

      if (completedForProperty.length === 0) {
        setStatus('error');
        setError('No completed report found for this property. Try Fresh Scrape.');
        return;
      }

      const results = new Map<number, ParityAnalysis>();

      for (const job of completedForProperty) {
        const nights = job.numberOfNights ?? DEFAULT_NIGHTS;
        if (!results.has(nights)) {
          const result = await analyzeAndShow(job.jobId, propertyName, currencyCode);
          results.set(nights, result);
        }
      }

      setAnalyses(results);
      const bestDefault = results.has(DEFAULT_NIGHTS) ? DEFAULT_NIGHTS : results.keys().next().value ?? DEFAULT_NIGHTS;
      setSelectedNights(bestDefault);
      setStatus('done');
      setProgress(100);

      fetchSupplementaryData(propertyName);
    },
    [cleanup, analyzeAndShow, fetchSupplementaryData],
  );

  const onUseLatest = async () => {
    const locationUrl = getValues('locationUrl');
    const currencyCode = getValues('currencyCode');
    if (!locationUrl) {
      return;
    }

    try {
      const propName = selectedProperty.current?.name || locationUrl;
      await loadReportForProperty(locationUrl, currencyCode, propName);
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onSelectRecentScrape = useCallback(
    async (job: PriceSummaryJob) => {
      const propertyName = parsePropertyNameFromTAURL(job.locationUrl) ?? job.locationUrl;
      setValue('locationUrl', job.locationUrl);
      setValue('currencyCode', job.currencyCode);
      setValue('countryCode', job.countryCode);

      try {
        await loadReportForProperty(job.locationUrl, job.currencyCode, propertyName);
      } catch (e) {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadReportForProperty, setValue],
  );

  const onFreshScrape = async (data: FormInput) => {
    try {
      cleanup();
      setError(null);
      setAnalyses(new Map());
      setRevenueHooks(null);
      setVendorAccountId(undefined);
      setStatus('submitting');
      setProgress(5);
      setProgressText(`Submitting ${NIGHT_COUNTS.length} scrape jobs (1-${NIGHT_COUNTS.length} nights)...`);
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + Math.min(data.daysAhead, 364));

      const jobIds = new Map<number, string>();

      const submissions = NIGHT_COUNTS.map(async (nights) => {
        const {
          result: { job_id: jobId },
        } = await PriceParrotService.postPriceSummaryJob({
          location_url: data.locationUrl,
          start_date: formatDateISO(startDate),
          end_date: formatDateISO(endDate),
          number_of_nights: nights,
          currency_code: data.currencyCode,
          country_code: data.countryCode,
        });
        jobIds.set(nights, jobId);
      });
      await Promise.all(submissions);

      const propName = selectedProperty.current?.name || data.locationUrl;

      setStatus('scraping');
      setProgress(10);
      setProgressText('Scraping prices for 1-5 nights...');

      const completedResults = new Map<number, ParityAnalysis>();
      const failedJobs = new Set<number>();

      const poll = async () => {
        try {
          const { data: allJobs } = await PriceParrotService.getPriceSummaryJobs({ limit: 100 });

          for (const [nights, jobId] of jobIds.entries()) {
            if (completedResults.has(nights) || failedJobs.has(nights)) {
              continue;
            }

            const job = allJobs.find((j) => j.jobId === jobId);
            if (!job) {
              continue;
            }

            if (job.status === PriceSummaryJobStatus.COMPLETED) {
              const result = await analyzeAndShow(jobId, propName, data.currencyCode);
              completedResults.set(nights, result);
            } else if (job.status === PriceSummaryJobStatus.FAILED || job.status === PriceSummaryJobStatus.CANCELLED) {
              failedJobs.add(nights);
            }
          }

          const totalDone = completedResults.size + failedJobs.size;
          const pct = Math.round((totalDone / NIGHT_COUNTS.length) * 80) + 10;
          setProgress(pct);
          setProgressText(
            `Scraping prices... ${completedResults.size}/
            ${NIGHT_COUNTS.length} complete${failedJobs.size > 0 ? `, ${failedJobs.size} failed` : ''}`,
          );

          if (totalDone === NIGHT_COUNTS.length) {
            if (completedResults.size === 0) {
              setStatus('error');
              setError('All scrape jobs failed');
              return;
            }

            setAnalyses(new Map(completedResults));
            const bestDefault = completedResults.has(DEFAULT_NIGHTS)
              ? DEFAULT_NIGHTS
              : completedResults.keys().next().value ?? DEFAULT_NIGHTS;
            setSelectedNights(bestDefault);
            setStatus('done');
            setProgress(100);

            fetchSupplementaryData(propName);
          } else {
            pollRef.current = setTimeout(poll, 5000);
          }
        } catch (e) {
          setStatus('error');
          setError(e instanceof Error ? e.message : String(e));
        }
      };
      pollRef.current = setTimeout(poll, 5000);
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleGenerateEmail = useCallback(() => {
    setEmailExpanded(true);
    setTimeout(() => {
      emailRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleExportPdf = useCallback(() => {
    if (reportRef.current && currentAnalysis) {
      exportToPdf(reportRef.current, currentAnalysis.propertyName, currentAnalysis.analysisDate);
    }
  }, [currentAnalysis]);

  const isWorking = ['submitting', 'scraping', 'analyzing'].includes(status);

  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
        Generate a parity report
      </Typography>
      <form onSubmit={handleSubmit(onFreshScrape)}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={4}>
            <LocationAutocomplete
              control={control}
              name="locationUrl"
              label="Property"
              rules={{ required: 'Required' }}
              onPropertySelect={(p) => {
                selectedProperty.current = p;
              }}
            />
          </Grid>
          <Grid item xs={1.5}>
            <Controller
              name="daysAhead"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Days Ahead"
                  type="number"
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 7, max: 364 } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={1.5}>
            <Controller
              name="currencyCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Currency" size="small" fullWidth select>
                  {getAllCurrencyCodes().map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={1.5}>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Country" size="small" fullWidth select>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.code}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={3.5}>
            <Box sx={{ display: 'flex', gap: 1.5, height: 40 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={onUseLatest}
                disabled={isWorking}
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                Use Latest
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={isWorking}
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                Fresh Scrape
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Divider sx={{ my: 3 }} />

      {currentAnalysis && availableNights.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            py: 2,
            px: 3,
            bgcolor: 'grey.100',
            borderRadius: '12px',
            mb: 3,
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', mr: 1 }}>
            Number of Nights:
          </Typography>
          {availableNights.map((n) => (
            <Box
              key={n}
              component="span"
              onClick={() => setSelectedNights(n)}
              sx={{
                px: 2.5,
                py: 1.25,
                borderRadius: '8px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                border: 2,
                borderColor: selectedNights === n ? 'primary.main' : 'grey.400',
                bgcolor: selectedNights === n ? 'primary.main' : 'white',
                color: selectedNights === n ? 'white' : 'text.primary',
                userSelect: 'none',
                transition: 'all 0.2s',
              }}
            >
              {n} {n === 1 ? 'Night' : 'Nights'}
            </Box>
          ))}
        </Box>
      )}

      {isWorking && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {progressText}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!currentAnalysis && !isWorking && !error && <RecentScrapes onSelectScrape={onSelectRecentScrape} />}

      {currentAnalysis && (
        <>
          <Box sx={{ mt: 4 }} ref={reportRef}>
            <ParityReport
              analysis={currentAnalysis}
              revenueHooks={revenueHooks}
              ratePlans={ratePlans}
              ratePlansLoading={ratePlansLoading}
              ratePlansError={ratePlansError}
              onGenerateEmail={handleGenerateEmail}
              onExportPdf={handleExportPdf}
            />
          </Box>
          <Box ref={emailRef}>
            <EmailGenerator
              analysis={currentAnalysis}
              revenueHooks={revenueHooks}
              ratePlanName={cheapestPlan?.name ?? ''}
              ratePlanCode={cheapestPlan?.rate_plan_code ?? ''}
              expanded={emailExpanded}
              onExpandedChange={setEmailExpanded}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
